import { css } from "@emotion/css";
import type { StoryFn } from "@storybook/react-vite";
import { Form, Formik } from "formik";
import { CSSProperties } from "react";

import { PrimaryButton } from "../components/button/PrimaryButton";
import { CascadiaProvider } from "../context/CascadiaProvider";
import { Container } from "../foundations/container/Container";
import { Grid } from "../foundations/grid-layout/Grid";
import { GridItem } from "../foundations/grid-layout/GridItem";
import { Layout } from "../foundations/grid-layout/Layout";

export function Centered(Story: StoryFn, context: any) {
  return <div className={styles.centered}>{Story(context.args, context)}</div>;
}

export function withLayout(Story: StoryFn, context: any) {
  return <Layout>{Story(context.args, context)}</Layout>;
}

export function withNarrowContainer(Story: StoryFn, context: any) {
  return <Container layout="narrow">{Story(context.args, context)}</Container>;
}
export function withWideContainer(Story: StoryFn, context: any) {
  return <Container layout="wide">{Story(context.args, context)}</Container>;
}

export function withCascadiaProvider(Story: StoryFn, context: any) {
  return (
    <CascadiaProvider locale="en">
      {Story(context.args, context)}
    </CascadiaProvider>
  );
}

export function withMaxWidth(maxWidth: CSSProperties["maxWidth"]) {
  return (Story: StoryFn, context: any) => (
    <div style={{ maxWidth }}>{Story(context.args, context)}</div>
  );
}

export function withHeight(maxHeight: number) {
  return (Story: StoryFn, context: any) => (
    <div
      style={{
        height: `${maxHeight}px`,
        overflow: "hidden",
        border: "1px solid black",
      }}
    >
      {Story(context.args, context)}
    </div>
  );
}

interface WithFormikProps {
  Story: StoryFn;
  initialValues?: Record<string, any>;
  validationSchema?: any;
  context?: any;
}

export function withFormikWrapper({
  Story,
  initialValues,
  validationSchema,
}: Omit<WithFormikProps, "context">) {
  // Storybook passes (Story, context) to decorators
  return function Decorator(StoryFn: StoryFn, context: any) {
    return (
      <WithFormik
        Story={StoryFn}
        context={context}
        initialValues={initialValues}
        validationSchema={validationSchema}
      />
    );
  };
}

function WithFormik({
  Story,
  initialValues = {},
  validationSchema,
  context = { args: {}, parameters: {} },
}: WithFormikProps) {
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(value): void => {
        alert(
          "Form submitted with value:\n " + JSON.stringify(value, undefined, 2)
        );
      }}
    >
      {({ handleSubmit }) => (
        <Form>
          <Grid form width="416px">
            {Story(context.args, context)}
            <GridItem>
              <PrimaryButton onPress={() => handleSubmit()} size="medium">
                Submit
              </PrimaryButton>
            </GridItem>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}

const styles = {
  centered: css({
    display: "flex",
    flex: 1,
    placeContent: "center",
    alignItems: "center",
    height: "100vh",
  }),
};
